import { useSyncExternalStore } from 'react';

export type ToastTone = 'info' | 'warn' | 'error';

export interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
  /** A second line for the detail nobody should have to guess at. */
  detail?: string;
}

/**
 * A five-line toast store instead of a library.
 *
 * The list is capped: toast libraries keep their history, and this tab stays
 * open for a whole shift. Three is also as many as anyone reads.
 */
const MAX_VISIBLE = 3;
const DEFAULT_TTL = 5000;

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function publish(): void {
  for (const listener of listeners) listener();
}

export function toast(tone: ToastTone, text: string, detail?: string, ttl = DEFAULT_TTL): number {
  const id = nextId++;
  toasts = [...toasts, { id, tone, text, detail }].slice(-MAX_VISIBLE);
  publish();
  setTimeout(() => {
    dismissToast(id);
  }, ttl);
  return id;
}

export function dismissToast(id: number): void {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  publish();
}

/** Test seam. */
export function clearToasts(): void {
  toasts = [];
  publish();
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    () => toasts,
    () => toasts,
  );
}
